import { motion, useReducedMotion } from 'framer-motion'
import { Button } from '@/ui/components'
import { MEDIA_CONFIG } from '@/shared/utils/media'
import { cn } from '@/shared/utils'

const HERO_LINES = ['Plan the route.', 'Bring the crew.', 'Keep your spots.']

const PROBLEM_CHIPS = ['Scattered threads', 'Buried details', 'No source of truth']

export function Landing3DHero() {
  const reducedMotion = useReducedMotion() ?? false

  const lineVariant = {
    hidden: reducedMotion ? { opacity: 1, y: 0 } : { opacity: 0, y: 28 },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: { delay: reducedMotion ? 0 : 0.15 + i * 0.12, duration: 0.6, ease: [0.22, 1, 0.36, 1] as const },
    }),
  }

  return (
    <section className="relative min-h-[92svh] w-full overflow-hidden bg-landing-base">
      {/* Video background — poster is the LCP element, video plays on top */}
      {reducedMotion ? (
        <img
          src={MEDIA_CONFIG.hero.image.src}
          alt="Aerial view of overlanding rigs on a backcountry route"
          className="absolute inset-0 h-full w-full object-cover"
          // @ts-expect-error fetchpriority is valid HTML but missing from React 18's img typings
          fetchpriority="high"
        />
      ) : (
        <video
          className="absolute inset-0 h-full w-full object-cover"
          src={MEDIA_CONFIG.hero.video.src}
          poster={MEDIA_CONFIG.hero.video.poster}
          autoPlay
          muted
          loop
          playsInline
        />
      )}

      {/* Scrim — bottom fades into the page background so hero flows into the wizard */}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-landing-base/80 via-black/40 to-landing-base" />

      {/* Copy overlay */}
      <div className="relative z-10 flex min-h-[92svh] flex-col justify-end px-6 pb-16 pt-32 sm:px-10 lg:px-16">
        <div className="mx-auto w-full max-w-app">
          <motion.span
            initial={reducedMotion ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="inline-block rounded-full border border-white/25 bg-white/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.25em] text-white/90 backdrop-blur-sm"
          >
            Now in Public Beta
          </motion.span>

          <h1 className="mt-6 text-5xl font-bold leading-[1.04] tracking-tight text-white sm:text-6xl lg:text-7xl xl:text-8xl">
            {HERO_LINES.map((line, i) => (
              <motion.span
                key={line}
                className={cn('block', i === 2 && 'text-landing-glow-soft [text-shadow:0_0_32px_hsl(var(--landing-glow)/0.45)]')}
                custom={i}
                variants={lineVariant}
                initial="hidden"
                animate="visible"
              >
                {line}
              </motion.span>
            ))}
          </h1>

          <motion.p
            initial={reducedMotion ? false : { opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: reducedMotion ? 0 : 0.55, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="mt-6 max-w-xl text-base leading-relaxed text-white/80 sm:text-lg"
          >
            The trip planner for overlanding crews — routes, meals, gear, and weather
            in one workspace, shared without giving away your spots.
          </motion.p>

          <motion.div
            initial={reducedMotion ? false : { opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: reducedMotion ? 0 : 0.68, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center"
          >
            <Button size="lg" className="w-full shadow-glow-sm sm:w-auto" asChild>
              <a href="/signup">Start planning free</a>
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="w-full border-white/40 bg-white/10 text-white backdrop-blur-sm hover:bg-white/20 hover:text-white sm:w-auto"
              asChild
            >
              <a href="#trip-builder">Build a demo trip</a>
            </Button>
          </motion.div>

          {/* Problem strip — what group chats get wrong, in one row */}
          <motion.div
            initial={reducedMotion ? false : { opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: reducedMotion ? 0 : 0.85, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="mt-14 flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-white/15 pt-5 text-sm"
          >
            <span className="font-medium text-white/70">Group chats bury the plan:</span>
            {PROBLEM_CHIPS.map((chip) => (
              <span key={chip} className="text-white/45 line-through decoration-white/40">
                {chip}
              </span>
            ))}
            <span className="font-semibold text-landing-glow-soft">SitRepp is the source of truth.</span>
          </motion.div>
        </div>
      </div>
    </section>
  )
}
